import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { integrationTimeEntryKeys } from "@/lib/query";

export type IntegrationTimeEntriesResponse = {
  configured: boolean;
  weeksSynced: number;
  lastSyncedAt: string | null;
};

export type RefreshTimeEntriesResult = {
  fetched: number;
  created: number;
  updated: number;
  unchanged: number;
  errors: number;
  unmapped: number;
  pagesFailed: number;
  partial: boolean;
  skipped: boolean;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useIntegrationTimeEntries() {
  return useQuery({
    queryKey: integrationTimeEntryKeys.list(),
    queryFn: () => jsonFetch<IntegrationTimeEntriesResponse>("/api/integrations/time-entries"),
  });
}

export function useRefreshTimeEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      jsonFetch<RefreshTimeEntriesResult>("/api/integrations/time-entries/refresh", {
        method: "POST",
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: integrationTimeEntryKeys.all });
      if (r.skipped) {
        toast.success("Time Entry sync skipped — no linked projects to sync.");
        return;
      }
      const summary =
        `Refreshed — ${r.fetched} fetched, ${r.created} new, ${r.updated} updated` +
        (r.unmapped > 0 ? `, ${r.unmapped} unmapped` : "") +
        (r.errors > 0 ? `, ${r.errors} errors` : "");
      // A partial pull synced what arrived but the gateway dropped some pages —
      // warn (not error) and tell the user another refresh will fill the gaps.
      if (r.partial) {
        const pages = `${r.pagesFailed} page${r.pagesFailed === 1 ? "" : "s"}`;
        toast.warning(
          `${summary}. ${pages} skipped — the Nymbl gateway didn't return them. Refresh again to fill the gaps.`,
        );
        return;
      }
      toast.success(summary);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
